import { type MousePosT } from '../../../providers/stage-provider';
import { type RectangleAnnotationT, type RectDataT } from '../../../types';
import { getTransformedRectDataPointsOnVertexMove } from '../multi-select/utils';
import { VERTEX_TYPE } from '../shape/vertex';
import type { VertexT } from '../shape/vertex/types';

// Utility function to constrain rectangle within stage boundaries
export const constrainRectangleToStageBounds = (rect: RectangleAnnotationT, stageWidth: number, stageHeight: number): {
  rect: RectangleAnnotationT;
  outOfBounds: boolean;
} => {
  // Get rectangle bounds
  const rectX = rect.data.x;
  const rectY = rect.data.y;
  const rectWidth = rect.data.width;
  const rectHeight = rect.data.height;

  // Check if any part of the rectangle would be outside the stage
  const outOfBounds = rectX < 0 || rectY < 0 || rectX + rectWidth > stageWidth || rectY + rectHeight > stageHeight;
  if (!outOfBounds) {
    return {
      rect,
      outOfBounds: false
    };
  }

  // Rectangle would be out of bounds, constrain it to stay within the stage
  const constrainedRect = {
    ...rect
  };
  if (rectX < 0) constrainedRect.data.x = 0;
  if (rectY < 0) constrainedRect.data.y = 0;
  if (rectX + rectWidth > stageWidth) {
    // If width would make it go out of bounds, adjust width or x position
    if (constrainedRect.data.x === 0) {
      // Adjust width to fit the stage
      constrainedRect.data.width = stageWidth;
    } else {
      // Adjust x position to keep width
      constrainedRect.data.x = stageWidth - rectWidth;
    }
  }
  if (rectY + rectHeight > stageHeight) {
    // If height would make it go out of bounds, adjust height or y position
    if (constrainedRect.data.y === 0) {
      // Adjust height to fit the stage
      constrainedRect.data.height = stageHeight;
    } else {
      // Adjust y position to keep height
      constrainedRect.data.y = stageHeight - rectHeight;
    }
  }
  return {
    rect: constrainedRect,
    outOfBounds: true
  };
};
export const convertDeltaToMousePosition = ({
  x,
  y,
  deltaWidth,
  deltaHeight,
  vertexType
}): [number, number] => {
  if (vertexType === 'RIGHT' || vertexType === 'BOTTOM' || vertexType === 'BOTTOM_RIGHT') {
    return [x + deltaWidth, y + deltaHeight];
  }
  if (vertexType === 'LEFT' || vertexType === 'TOP' || vertexType === 'TOP_LEFT') {
    return [x - deltaWidth, y - deltaHeight];
  }
  if (vertexType === 'TOP_RIGHT') {
    return [x + deltaWidth, y - deltaHeight];
  }
  if (vertexType === 'BOTTOM_LEFT') {
    return [x - deltaWidth, y + deltaHeight];
  }
  return [x, y];
};
export const getRectangleVertices = (data: RectDataT): VertexT[] => {
  const {
    x,
    y,
    width,
    height
  } = data;
  return [{
    point: [x, y],
    type: VERTEX_TYPE.TOP_LEFT
  }, {
    point: [x + width / 2, y],
    type: VERTEX_TYPE.TOP
  }, {
    point: [x + width, y],
    type: VERTEX_TYPE.TOP_RIGHT
  }, {
    point: [x + width, y + height / 2],
    type: VERTEX_TYPE.RIGHT
  }, {
    point: [x + width, y + height],
    type: VERTEX_TYPE.BOTTOM_RIGHT
  }, {
    point: [x + width / 2, y + height],
    type: VERTEX_TYPE.BOTTOM
  }, {
    point: [x, y + height],
    type: VERTEX_TYPE.BOTTOM_LEFT
  }, {
    point: [x, y + height / 2],
    type: VERTEX_TYPE.LEFT
  }];
};
export const getDrawingRectCoords = (x0: number, y0: number, x1: number, y1: number) => {
  // Ensure x0, x1, y0, y1 are positive
  const boundedX1 = Math.max(x1, 0);
  const boundedX0 = Math.max(x0, 0);
  const boundedY1 = Math.max(y1, 0);
  const boundedY0 = Math.max(y0, 0);
  return {
    x: Math.min(boundedX0, boundedX1),
    y: Math.min(boundedY0, boundedY1),
    width: Math.abs(boundedX1 - boundedX0),
    height: Math.abs(boundedY1 - boundedY0)
  };
};
export const getTranslatedRectangle = (deltaX: number, deltaY: number, rectangle: RectangleAnnotationT): RectangleAnnotationT => {
  return {
    ...rectangle,
    data: {
      ...rectangle.data,
      x: rectangle.data.x + deltaX,
      y: rectangle.data.y + deltaY
    }
  };
};

/**
 * Core rotation transformation without center point translation
 * @param dx X offset from center
 * @param dy Y offset from center
 * @param angle Rotation angle in radians
 * @returns Rotated coordinates [x, y]
 */
const rotateVector = (dx: number, dy: number, angle: number): [number, number] => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [dx * cos - dy * sin, dx * sin + dy * cos];
};

/**
 * Transforms a point from global to local coordinates or vice versa
 * @param point The point to transform
 * @param center The center point of rotation
 * @param angle Rotation angle in radians
 * @param addCenter Whether to add center coordinates back after rotation
 * @param reverse Whether to reverse the rotation direction
 */
export const transformCoordinates = ({
  point,
  center,
  angle,
  addCenter = true,
  reverse = false
}: {
  point: MousePosT;
  center: MousePosT;
  angle: number;
  addCenter: boolean;
  reverse: boolean;
}): [number, number] => {
  // Calculate offset from center
  const dx = point[0] - center[0];
  const dy = point[1] - center[1];

  // Rotate the offset vector
  const rotationAngle = reverse ? -angle : angle;
  const [rx, ry] = rotateVector(dx, dy, rotationAngle);

  // Return with or without adding center back
  return addCenter ? [center[0] + rx, center[1] + ry] : [rx, ry];
};
export const transformPoint = ({
  point,
  centerCoords,
  rotation = 0,
  reverse = false
}: {
  point: MousePosT;
  centerCoords: MousePosT;
  rotation?: number;
  reverse?: boolean;
}): [number, number] => transformCoordinates({
  point,
  center: centerCoords,
  angle: rotation,
  addCenter: true,
  // add center back
  reverse
});
export const getRelativeMousePosition = (mousePos: MousePosT, rectCenter: MousePosT, rotation: number): MousePosT => transformCoordinates({
  point: mousePos,
  center: rectCenter,
  angle: rotation,
  addCenter: false,
  // don't add center back
  reverse: true // reverse rotation
});

// Handles vertex movement for rotated rectangles, calculating new dimensions and position
const getTransformedPointsOnRotatedVertexMove = ({
  rectangle,
  // Current rectangle being modified
  mousePos,
  // Current mouse position
  selectedVertexType,
  // The vertex (corner/edge) being dragged,
  initialRect = null,
  initialMousePos = null
}: {
  rectangle: RectangleAnnotationT;
  mousePos: MousePosT;
  selectedVertexType: (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE];
  initialRect: RectangleAnnotationT | null;
  initialMousePos: MousePosT | null;
}): RectangleAnnotationT => {
  // Initialize transformation if this is the first move
  if (!initialRect || !initialMousePos) {
    return rectangle;
  }

  // Extract initial rectangle properties
  const {
    x,
    y,
    width,
    height,
    rotationAngle = 0
  } = initialRect.data;
  // Calculate center point of the rectangle
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  // Convert initial and current mouse positions to rectangle's local coordinate system
  const initialRelPos = getRelativeMousePosition(initialMousePos, [centerX, centerY], rotationAngle);
  const currentRelPos = getRelativeMousePosition(mousePos, [centerX, centerY], rotationAngle);

  // Calculate mouse movement delta in local coordinates
  const dx = currentRelPos[0] - initialRelPos[0];
  const dy = currentRelPos[1] - initialRelPos[1];

  // Initialize new dimensions and center offsets
  let newWidth = width;
  let newHeight = height;
  let centerOffsetX = 0;
  let centerOffsetY = 0;

  // Minimum size to prevent rectangle from becoming too small
  const MIN_SIZE = 10;

  // Calculate new dimensions and offsets based on which vertex is being dragged
  switch (selectedVertexType) {
    case VERTEX_TYPE.TOP_LEFT:
      {
        // Decrease width and height when dragging top-left corner
        newWidth = Math.max(MIN_SIZE, width - dx);
        newHeight = Math.max(MIN_SIZE, height - dy);
        // Calculate center offset to maintain correct position
        centerOffsetX = (width - newWidth) / 2;
        centerOffsetY = (height - newHeight) / 2;
        break;
      }
    case VERTEX_TYPE.TOP:
      {
        // Only modify height when dragging top edge
        newHeight = Math.max(MIN_SIZE, height - dy);
        centerOffsetY = (height - newHeight) / 2;
        break;
      }
    case VERTEX_TYPE.TOP_RIGHT:
      {
        // Increase width, decrease height when dragging top-right corner
        newWidth = Math.max(MIN_SIZE, width + dx);
        newHeight = Math.max(MIN_SIZE, height - dy);
        centerOffsetX = (newWidth - width) / 2;
        centerOffsetY = (height - newHeight) / 2;
        break;
      }
    case VERTEX_TYPE.RIGHT:
      {
        // Only modify width when dragging right edge
        newWidth = Math.max(MIN_SIZE, width + dx);
        centerOffsetX = (newWidth - width) / 2;
        break;
      }
    case VERTEX_TYPE.BOTTOM_RIGHT:
      {
        // Increase both width and height when dragging bottom-right corner
        newWidth = Math.max(MIN_SIZE, width + dx);
        newHeight = Math.max(MIN_SIZE, height + dy);
        centerOffsetX = (newWidth - width) / 2;
        centerOffsetY = (newHeight - height) / 2;
        break;
      }
    case VERTEX_TYPE.BOTTOM:
      {
        // Only modify height when dragging bottom edge
        newHeight = Math.max(MIN_SIZE, height + dy);
        centerOffsetY = (newHeight - height) / 2;
        break;
      }
    case VERTEX_TYPE.BOTTOM_LEFT:
      {
        // Decrease width, increase height when dragging bottom-left corner
        newWidth = Math.max(MIN_SIZE, width - dx);
        newHeight = Math.max(MIN_SIZE, height + dy);
        centerOffsetX = (width - newWidth) / 2;
        centerOffsetY = (newHeight - height) / 2;
        break;
      }
    case VERTEX_TYPE.LEFT:
      {
        // Only modify width when dragging left edge
        newWidth = Math.max(MIN_SIZE, width - dx);
        centerOffsetX = (width - newWidth) / 2;
        break;
      }
  }

  // Convert center offset from local back to global coordinates using rotation matrix
  const cos = Math.cos(rotationAngle);
  const sin = Math.sin(rotationAngle);
  const globalOffsetX = centerOffsetX * cos - centerOffsetY * sin;
  const globalOffsetY = centerOffsetX * sin + centerOffsetY * cos;

  // Calculate final position in global coordinates
  const newCenterX = centerX + globalOffsetX;
  const newCenterY = centerY + globalOffsetY;
  const newX = newCenterX - newWidth / 2;
  const newY = newCenterY - newHeight / 2;

  // Return updated rectangle with new dimensions and position
  return {
    ...rectangle,
    data: {
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
      rotationAngle
    }
  };
};
export const getTransformedPointsOnVertexMove = (rectangle: RectangleAnnotationT, mousePos: MousePosT, selectedVertexType: (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE], multiSelectBox: RectDataT | null | undefined = null, initialRect: RectangleAnnotationT | null = null, initialMousePos: MousePosT | null = null): RectangleAnnotationT => {
  const rotationAngle = rectangle.data.rotationAngle;
  if (rotationAngle === 0.0) {
    const transformedRect = getTransformedRectDataPointsOnVertexMove(rectangle.data, mousePos, selectedVertexType, multiSelectBox);
    return {
      ...rectangle,
      data: {
        ...transformedRect
      }
    };
  }
  return getTransformedPointsOnRotatedVertexMove({
    rectangle,
    mousePos,
    selectedVertexType,
    initialRect,
    initialMousePos
  });
};
export const interpolateRectangle = (initialRect: RectDataT, finalRect: RectDataT, interpolationFactor: number): RectDataT => {
  return {
    x: initialRect.x + (finalRect.x - initialRect.x) * interpolationFactor,
    y: initialRect.y + (finalRect.y - initialRect.y) * interpolationFactor,
    width: initialRect.width + (finalRect.width - initialRect.width) * interpolationFactor,
    height: initialRect.height + (finalRect.height - initialRect.height) * interpolationFactor,
    rotationAngle: 0
  };
};